// src/components/mdx/client/MetricsComponents.client.tsx

'use client'

import { useState, type ReactNode } from 'react'
import { motion } from "motion/react"
import { cn } from '~/lib/utils'

// ------------ Types (duplicated locally to avoid cycles) ------------

// NOTE: no client-side "is mounted" SSR gate here. This leaf is rendered ONLY on
// the client, after hydration, by makeClientSSR's HydrationGate (the crawlable
// shadow is the server/no-JS output), so it never SSRs and the old
// `if (!isMounted) return null` guard is unnecessary — and harmful: it blanked
// the island for one paint right after the gate swapped in the freshly-loaded
// chunk (#372 r3461232324).
export interface MetricCardProps {
  label: string
  value: number | string
  benchmark?: number
  /** When true, a value BELOW the benchmark is the good (green) outcome — e.g. CPC/CPA, where lower is better. Defaults to higher-is-better. */
  lowerIsBetter?: boolean
  format?: 'percentage' | 'multiplier' | 'currency' | 'number'
  className?: string
}

export interface AdPerformanceMetricsProps {
  metrics: { ctr: number; cpc: number; conversionRate: number; roas: number }
  benchmarks?: { ctr: number; cpc: number; conversionRate: number; roas: number }
  className?: string
}

export interface EnhancedMetricCardProps extends MetricCardProps {
  subtitle?: string | ReactNode
}

export interface RetentionPoint { seconds: number; rate: number }

export interface VideoMetricsProps {
  metrics: {
    durationSeconds: number
    avgWatchSeconds: number
    completionRate: number
    retentionPoints: RetentionPoint[]
  }
  comparisonMetrics?: {
    avgWatchSeconds: number
    completionRate: number
    retentionPoints: RetentionPoint[]
  }
  comparison?: boolean
  className?: string
}

export interface MetricGridItem {
  key: string
  label: string
  value: number
  format: 'percentage' | 'currency' | 'multiplier' | 'number'
  subtitle?: string | ReactNode
  benchmark?: number
}

export interface MetricGridProps {
  metrics: MetricGridItem[]
  columns?: 1 | 2 | 3 | 4 | 6
  spacing?: 'tight' | 'normal' | 'loose'
  className?: string
}

export interface CreativeInsightProps {
  title: string
  description: string
  impact: 'positive' | 'negative' | 'neutral'
  icon?: ReactNode
  className?: string
}

// ------------ Helpers (client) ------------
const formatValue = (val: number | string, format: MetricCardProps['format'] = 'number'): string => {
  if (typeof val === 'string') return val
  
  // Handle NaN, undefined, null cases with comprehensive null checking
  if (val === null || val === undefined || Number.isNaN(val)) return '0'
  
  switch (format) {
    case 'percentage':
      return `${(val * 100).toFixed(1)}%`
    case 'multiplier':
      return `${val.toFixed(2)}x`
    case 'currency':
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val)
    case 'number':
    default:
      return new Intl.NumberFormat('en-US').format(val)
  }
}

const getPerformanceColor = (value: number, benchmark?: number, lowerIsBetter = false): string => {
  if (benchmark === undefined || benchmark === null) return 'text-gray-900 dark:text-white'
  const better = lowerIsBetter ? value <= benchmark : value >= benchmark
  return better ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
}

// ------------ Client components with simple, cohesive animations ------------

/**
 * Individual metric card component with simple entrance animation.
 * Uses the same animation pattern as the original for consistency.
 */
export function MetricCardClient({ label, value, benchmark, lowerIsBetter, format = 'number', className }: MetricCardProps): JSX.Element {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={cn(
        "rounded-lg bg-white p-4 shadow dark:bg-gray-800",
        "transition-colors duration-200",
        className
      )}
    >
      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
        {label}
      </dt>
      <dd className={cn(
        "mt-1 text-2xl font-semibold truncate",
        typeof value === 'number' ? getPerformanceColor(value, benchmark, lowerIsBetter) : "text-gray-900 dark:text-white"
      )}>
        {formatValue(value, format)}
      </dd>
      {benchmark !== undefined && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Benchmark: {formatValue(benchmark, format)}
        </p>
      )}
    </motion.div>
  )
}

/**
 * Ad performance metrics grid with simple coordinated animations.
 * Uses the same pattern as the original for consistency.
 */
export function AdPerformanceMetricsClient({ metrics, benchmarks, className }: AdPerformanceMetricsProps): JSX.Element {
  const metricConfigs = [
    { key: 'ctr', label: 'CTR', format: 'percentage' as const, lowerIsBetter: false },
    { key: 'cpc', label: 'CPC', format: 'currency' as const, lowerIsBetter: true },
    { key: 'conversionRate', label: 'Conversion Rate', format: 'percentage' as const, lowerIsBetter: false },
    { key: 'roas', label: 'ROAS', format: 'multiplier' as const, lowerIsBetter: false },
  ]
  
  return (
    <div className={cn('grid grid-cols-2 gap-4 sm:grid-cols-4', className)}>
      {metricConfigs.map(({ key, label, format, lowerIsBetter }) => (
        <MetricCardClient
          key={key}
          label={label}
          value={metrics[key as keyof typeof metrics]}
          benchmark={benchmarks?.[key as keyof typeof benchmarks]}
          format={format}
          lowerIsBetter={lowerIsBetter}
        />
      ))}
    </div>
  )
}

/**
 * Enhanced metric card with subtitle support.
 * Wraps MetricCardClient with additional subtitle information.
 */
export function EnhancedMetricCardClient({ subtitle, ...props }: EnhancedMetricCardProps): JSX.Element {
  return (
    <div className="space-y-1">
      <MetricCardClient {...props} />
      {subtitle && (
        <p className="text-xs text-gray-500 dark:text-gray-400 italic">
          {subtitle}
        </p>
      )}
    </div>
  )
}

/**
 * Configurable metric grid with responsive layout and spacing options.
 * Uses simple animations consistent with the original.
 */
export function MetricGridClient({ metrics, columns = 4, spacing = 'normal', className }: MetricGridProps): JSX.Element {
  const spacingStyles = { tight: 'gap-2', normal: 'gap-4', loose: 'gap-6' } as const
  const columnStyles: Record<number, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-3',
    4: 'grid-cols-2 sm:grid-cols-4',
    6: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6',
  }
  
  return (
    <div className={cn('grid', columnStyles[columns], spacingStyles[spacing], className)}>
      {metrics.map((m) => (
        <EnhancedMetricCardClient
          key={m.key}
          label={m.label}
          value={m.value}
          format={m.format}
          subtitle={m.subtitle}
          benchmark={m.benchmark}
        />
      ))}
    </div>
  )
}

/**
 * Video metrics component with simple, cohesive animations.
 * Restores the original animation behavior for consistency.
 */
export function VideoMetricsClient({ metrics, comparisonMetrics, comparison = false, className }: VideoMetricsProps): JSX.Element | null {
  const [showComparison, setShowComparison] = useState(false)

  // Format duration helper with comprehensive edge case handling
  const formatDuration = (seconds: number): string => {
    // Handle invalid inputs gracefully
    if (!seconds || seconds < 0 || Number.isNaN(seconds)) return '0s'
    return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`
  }

  // Calculate percentage watched with safe division
  const percentageWatched = metrics.durationSeconds > 0 
    ? (metrics.avgWatchSeconds / metrics.durationSeconds) * 100 
    : 0
  const comparisonPercentageWatched = comparisonMetrics && metrics.durationSeconds > 0
    ? (comparisonMetrics.avgWatchSeconds / metrics.durationSeconds) * 100
    : null

  // Normalize retention points arrays with comprehensive null checking
  const normalizedRetentionPoints = metrics.retentionPoints ?? []
  const normalizedComparisonPoints = comparisonMetrics?.retentionPoints?.slice(0, normalizedRetentionPoints.length) ?? []

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={cn(
        "space-y-6 rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800/90",
        "backdrop-blur-sm transition-all duration-200",
        className
      )}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Video Performance</h3>
        {comparison && comparisonMetrics && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {showComparison ? 'Showing comparison' : 'Showing original'}
            </span>
            <button
              onClick={() => setShowComparison(!showComparison)}
              className={cn(
                "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-colors",
                showComparison 
                  ? "bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              )}
            >
              <span className="mr-1">↔</span>
              Compare
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <EnhancedMetricCardClient
          label="Average Watch Time"
          value={showComparison && comparisonMetrics ? comparisonMetrics.avgWatchSeconds : metrics.avgWatchSeconds}
          format="number"
          subtitle={<>
            <span className="font-medium">
              {(showComparison && comparisonMetrics ? comparisonPercentageWatched : percentageWatched)?.toFixed(1)}%
            </span> of total duration 
            <span className="text-gray-400"> • </span>
            <span className="text-gray-400">{formatDuration(metrics.durationSeconds)} total</span>
          </>}
        />
        <EnhancedMetricCardClient
          label="Completion Rate"
          value={showComparison && comparisonMetrics ? comparisonMetrics.completionRate : metrics.completionRate}
          format="percentage"
          subtitle={<>Viewers who watched <span className="font-medium">95%+</span> of content</>}
        />
      </div>

      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Audience Retention
        </h4>
        <div className="relative space-y-2">
          {normalizedRetentionPoints.map((point, index) => {
            const comparisonPoint = normalizedComparisonPoints[index]
            const currentPoint = showComparison && comparisonPoint ? comparisonPoint : point
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group relative flex items-center gap-3"
              >
                <span className="min-w-[60px] text-sm tabular-nums text-gray-600 dark:text-gray-400">
                  {formatDuration(point.seconds)}
                </span>
                <div className="flex flex-1 items-center gap-3">
                  <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700/50">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${currentPoint.rate * 100}%` }}
                      transition={{ duration: 0.5 }}
                      className={cn(
                        "h-full transition-colors",
                        currentPoint.rate > 0.7 ? "bg-green-500 dark:bg-green-400" :
                        currentPoint.rate > 0.4 ? "bg-blue-500 dark:bg-blue-400" :
                        "bg-orange-500 dark:bg-orange-400"
                      )}
                    />
                  </div>
                  <span className="min-w-[70px] text-sm font-medium tabular-nums text-gray-700 dark:text-gray-300">
                    {(currentPoint.rate * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="absolute left-1/2 top-0 hidden -translate-x-1/2 -translate-y-full rounded bg-gray-900 px-2 py-1 text-xs text-white group-hover:block">
                  {(currentPoint.rate * 100).toFixed(1)}% retained at {formatDuration(point.seconds)}
                </div>
              </motion.div>
            )
          })}
        </div>
      </div>
    </motion.div>
  )
}

/**
 * Creative insight component with impact-based styling and entrance animation.
 * Uses simple animation consistent with the original.
 */
export function CreativeInsightClient({ title, description, impact, icon, className }: CreativeInsightProps): JSX.Element {
  const impactStyles = {
    positive: 'bg-green-50 border-green-500 dark:bg-green-900/20',
    negative: 'bg-red-50 border-red-500 dark:bg-red-900/20',
    neutral:  'bg-gray-50 border-gray-500 dark:bg-gray-900/20',
  } as const

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={cn(
        'rounded-lg border-l-4 p-4 transition-colors duration-200', 
        impactStyles[impact], 
        className
      )}
    >
      <div className="flex items-start">
        {icon && <span className="mr-3 mt-1 flex-shrink-0 text-[1.3em]">{icon}</span>}
        <div className="min-w-0 flex-1">
          <h4 className="text-lg font-semibold text-gray-900 dark:text-white truncate">{title}</h4>
          <p className="mt-1 text-gray-600 dark:text-gray-300">{description}</p>
        </div>
      </div>
    </motion.div>
  )
}